<template>
  <v-card>
    <v-card-title>Types</v-card-title>
    <v-card-text>
      <v-fade-transition>
        <v-alert
          v-show="actionAlert"
          border="left"
          color="success"
          dark
          text
          dismissible
        >
          {{ actionAlertMessage }}
        </v-alert>
      </v-fade-transition>
    </v-card-text>
    <v-card-text class="d-flex align-center flex-wrap pb-0">
      <!-- search -->
      <v-text-field
        v-model="searchQuery"
        placeholder="Search"
        outlined
        hide-details
        dense
        clearable
        class="user-search me-3 mb-4"
        @keyup.enter="fetchTypes"
        @click:clear="clearSearch"
      >
      </v-text-field>

      <v-spacer></v-spacer>

      <div class="d-flex align-center flex-wrap">
        <v-btn
          v-if="$store.getters['auth/can'](['create vehicles'])"
          color="primary"
          class="mb-4 me-3"
          @click="typeData = {};typesEditorDialog = !typesEditorDialog"
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
          <span>Add New Type</span>
        </v-btn>

        <!-- <v-btn
          color="secondary"
          outlined
          class="mb-4"
          disabled
        >
          <v-icon
            size="17"
            class="me-1"
          >
            {{ icons.mdiExportVariant }}
          </v-icon>
          <span>Export</span>
        </v-btn> -->
      </div>
    </v-card-text>
    <v-card-text>
      <v-data-table
        :headers="headers"
        :items="types"
        :options.sync="options"
        :server-items-length="totalTypes"
        :footer-props="footerProps"
        @page-count="pageCount = $event"
      >
        <!-- type -->
        <template #[`item.type`]="{ item }">
          <span class="d-block font-weight-semibold text-truncate text--primary">{{ item.type }}</span>
        </template>

        <!-- actions -->
        <template #[`item.actions`]="{item}">
          <v-menu
            bottom
            left
          >
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>{{ icons.mdiDotsVertical }}</v-icon>
              </v-btn>
            </template>

            <v-list>
              <v-list-item
                v-if="$store.getters['auth/can'](['edit vehicles'])"
                @click="editItem(item)"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiPencilOutline }}
                  </v-icon>
                  <span>Edit</span>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                v-if="$store.getters['auth/can'](['delete vehicles'])"
                @click="deleteItemConfirm(item)"
              >
                <v-list-item-title>
                  <v-icon
                    size="20"
                    class="me-2"
                  >
                    {{ icons.mdiDeleteOutline }}
                  </v-icon>
                  <span>Delete</span>
                </v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>
        </template>
      </v-data-table>
    </v-card-text>
    <types-editor
      :types-editor-dialog.sync="typesEditorDialog"
      :type-data="typeData"
      @saveType="saveType"
    ></types-editor>
    <!-- dialog -->
    <v-dialog
      v-model="dialogDelete"
      max-width="500px"
    >
      <v-card>
        <v-card-title>
          Are you sure you want to delete this item?
        </v-card-title>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            outlined
            @click="dialogDelete = !dialogDelete"
          >
            Cancel
          </v-btn>
          <v-btn
            color="success"
            @click="deleteItem"
          >
            OK
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import {
  mdiSquareEditOutline,
  mdiDotsVertical,
  mdiPlus,
  mdiExportVariant,
  mdiPencilOutline,
  mdiDeleteOutline,
} from '@mdi/js'
import { onMounted, ref, watch } from '@vue/composition-api'
import axios from '@axios'
import TypesEditor from './TypesEditor.vue'
import useTypes from './useTypes'
import useUIResolver from '../useUIResolver'

const defaultAlertMessage = 'Type successfully saved.'

export default {
  components: {
    TypesEditor,
  },
  setup() {
    const typeData = ref({})
    const actionAlert = ref(false)
    const actionAlertMessage = ref('')
    const typesEditorDialog = ref(false)
    const searchQuery = ref('')
    const totalTypes = ref(0)
    const footerProps = ref({
      itemsPerPageOptions: [15, 50, 100],
    })
    const options = ref({
      groupBy: [],
      groupDesc: [],
      itemsPerPage: 15,
      multiSort: false,
      mustSort: false,
      page: 1,
      sortBy: ['name'],
      sortDesc: [],
    })
    const dialogDelete = ref(false)

    onMounted(() => {
      actionAlertMessage.value = defaultAlertMessage
    })

    const { types, getTypes } = useTypes()
    const { typeTableHeaders } = useUIResolver()

    const fetchTypes = () => {
      getTypes({ ...options.value, ...{ searchQuery: searchQuery.value } }).then(response => {
        const { total } = response.data
        totalTypes.value = total
        options.value.itemsPerPage = response.data.per_page
      })
    }

    const clearSearch = () => {
      searchQuery.value = null
      fetchTypes()
    }

    const saveType = () => {
      actionAlertMessage.value = defaultAlertMessage
      actionAlert.value = true
      fetchTypes()
    }

    const editItem = item => {
      // show types-editor dialog
      typesEditorDialog.value = true

      // assign the select type
      typeData.value = item
    }

    const deleteItem = () => {
      axios.delete(`/vehicle-types/${typeData.value.id}`).then(response => {
        actionAlertMessage.value = 'Type successfully deleted.'
        actionAlert.value = true

        // hide confirm alert
        dialogDelete.value = false
        fetchTypes()
      })
    }

    const deleteItemConfirm = item => {
      typeData.value = item
      dialogDelete.value = true
    }

    watch(options, fetchTypes)

    watch(actionAlert, val => {
      if (val) {
        setTimeout(() => {
          actionAlert.value = false
        }, 3000)
      }
    })

    return {
      dialogDelete,
      actionAlert,
      actionAlertMessage,
      typeData,
      headers: typeTableHeaders(),
      searchQuery,
      totalTypes,
      footerProps,
      options,

      // useTypes
      types,
      getTypes,

      // functions
      clearSearch,
      typesEditorDialog,
      saveType,
      fetchTypes,

      editItem,
      deleteItem,
      deleteItemConfirm,

      // icons
      icons: {
        mdiSquareEditOutline,
        mdiDotsVertical,
        mdiPlus,
        mdiExportVariant,
        mdiPencilOutline,
        mdiDeleteOutline,
      },
    }
  },
}
</script>
